import { useEffect, useRef } from 'react';
import { SlotInfo, SlotType } from '../../../types/schedule.types';
import { SceneInfo, SceneWithUserNames } from '../../../types/scene.types';
import { BatchSlotLocationVisibilityItem } from '../../../api/schedule';

interface UseAutoFixSlotVisibilityOnSceneLocationChangeParams {
  scheduleID: string | undefined;
  scheduleBelongBreakdownID: string | undefined;
  scenes: SceneWithUserNames[];
  optimisticSlots: SlotInfo[];
  getBreakdownCache: (breakdownID: string) => { scenes?: SceneWithUserNames[] } | null;
  subscribeBreakdownCache: (breakdownID: string, cb: () => void) => () => void;
  batchPatchSlotLocationVisibilityData: (
    scheduleID: string,
    updates: BatchSlotLocationVisibilityItem[],
  ) => Promise<void>;
  getCalendarRowIdsForDateStr: (dateStr: string) => string[];
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
}

/**
 * sceneLocation 变化后自动修补 visibility：
 * 1) 关联任务自身 N -> Y
 * 2) 其后方任务若按“变更前地点”同址且为 N -> Y
 */
export function useAutoFixSlotVisibilityOnSceneLocationChange({
  scheduleID,
  scheduleBelongBreakdownID,
  scenes,
  optimisticSlots,
  getBreakdownCache,
  subscribeBreakdownCache,
  batchPatchSlotLocationVisibilityData,
  getCalendarRowIdsForDateStr,
  getSceneInfo,
}: UseAutoFixSlotVisibilityOnSceneLocationChangeParams): void {
  const prevSceneLocationMapRef = useRef<Map<string, string>>(new Map());
  const autoFixingVisibilityFromSceneLocationRef = useRef(false);

  useEffect(() => {
    if (!scenes?.length) return;
    const prevMap = prevSceneLocationMapRef.current;
    for (const s of scenes) {
      if (s.sceneID) prevMap.set(s.sceneID, s.sceneLocation ?? '');
    }
  }, [scenes]);

  useEffect(() => {
    if (!scheduleBelongBreakdownID || !scheduleID) return;

    const breakdownID = scheduleBelongBreakdownID;
    const syncScenesFromCache = async () => {
      const cachedData = getBreakdownCache(breakdownID);
      const cachedScenes = cachedData?.scenes ?? [];
      const prevMap = prevSceneLocationMapRef.current;
      const prevMapSnapshot = new Map(prevMap);
      const changedSceneIds = new Set<string>();

      for (const s of cachedScenes) {
        if (!s.sceneID) continue;
        const nextLoc = s.sceneLocation ?? '';
        const prevLoc = prevMapSnapshot.get(s.sceneID);
        if (prevLoc !== undefined && prevLoc !== nextLoc) {
          changedSceneIds.add(s.sceneID);
        }
      }

      for (const s of cachedScenes) {
        if (!s.sceneID) continue;
        prevMap.set(s.sceneID, s.sceneLocation ?? '');
      }

      if (changedSceneIds.size === 0) return;
      if (autoFixingVisibilityFromSceneLocationRef.current) return;

      autoFixingVisibilityFromSceneLocationRef.current = true;
      try {
        const isSoltRow = (slot: SlotInfo | undefined): slot is SlotInfo =>
          !!slot && (slot.slotType === SlotType.SHOOT || !slot.slotType);
        const isStoredN = (v: string | null | undefined) => String(v ?? '').toUpperCase() === 'N';

        const slotMap = new Map(optimisticSlots.map((t) => [t.slotID, t]));
        const sceneMap = new Map(cachedScenes.map((s) => [s.sceneID, s]));
        const dateOrderedIdsCache = new Map<string, string[]>();
        const toYIds = new Set<string>();

        const getSceneLocationBeforeChange = (sceneID: string | undefined): string => {
          if (!sceneID) return '';
          if (changedSceneIds.has(sceneID)) {
            return (prevMapSnapshot.get(sceneID) ?? '').trim().toLowerCase();
          }
          const nextScene = sceneMap.get(sceneID) ?? getSceneInfo(sceneID);
          return (nextScene?.sceneLocation ?? '').trim().toLowerCase();
        };

        const relatedSolts = optimisticSlots.filter((t) => {
          if (!isSoltRow(t)) return false;
          if (!t.slotBelongSceneID || !changedSceneIds.has(t.slotBelongSceneID)) return false;
          if (!t.slotBelongScheduleID) return true;
          return t.slotBelongScheduleID === scheduleID;
        });

        for (const slot of relatedSolts) {
          if (isStoredN(slot.slotLocationVisibility)) {
            toYIds.add(slot.slotID);
          }

          if (!slot.slotDate) continue;
          const dateStr = slot.slotDate.split('T')[0];
          if (!dateOrderedIdsCache.has(dateStr)) {
            dateOrderedIdsCache.set(dateStr, getCalendarRowIdsForDateStr(dateStr));
          }
          const orderedIds = dateOrderedIdsCache.get(dateStr) ?? [];
          const idx = orderedIds.indexOf(slot.slotID);
          if (idx === -1 || idx >= orderedIds.length - 1) continue;

          const follower = slotMap.get(orderedIds[idx + 1]);
          if (!isSoltRow(follower)) continue;
          if (!isStoredN(follower.slotLocationVisibility)) continue;

          const slotLocBefore = getSceneLocationBeforeChange(slot.slotBelongSceneID);
          const followerLocBefore = getSceneLocationBeforeChange(follower.slotBelongSceneID);
          if (slotLocBefore && slotLocBefore === followerLocBefore) {
            toYIds.add(follower.slotID);
          }
        }

        if (toYIds.size > 0) {
          await batchPatchSlotLocationVisibilityData(
            scheduleID,
            Array.from(toYIds).map((slotID) => ({
              slotID,
              slotLocationVisibility: 'Y',
            })),
          );
        }
      } catch (err) {
        console.error('[Schedule] 场次地点变更后自动修补 slotLocationVisibility 失败:', err);
      } finally {
        autoFixingVisibilityFromSceneLocationRef.current = false;
      }
    };

    void syncScenesFromCache();
    const unsubscribe = subscribeBreakdownCache(breakdownID, () => {
      void syncScenesFromCache();
    });
    return unsubscribe;
  }, [
    scheduleBelongBreakdownID,
    scheduleID,
    getBreakdownCache,
    subscribeBreakdownCache,
    optimisticSlots,
    batchPatchSlotLocationVisibilityData,
    getCalendarRowIdsForDateStr,
    getSceneInfo,
  ]);
}

